import {
  AlertVariant,
  Bullseye,
  Button,
  EmptyState,
  EmptyStateBody,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalVariant,
  PageSection,
  Spinner,
  Tab,
  TabTitleText,
  Tabs
} from '@patternfly/react-core';
import { CommentIcon, OutlinedCommentsIcon } from '@patternfly/react-icons';
import * as React from 'react';
import { useParams } from 'react-router-dom';

import { useNotification } from '@app/AppLayout/NotificationContext';
import { SurveyApi } from '@app/api';
import { DataLoadError } from '@app/components/DataLoadError';
import { DetailPageHeader } from '@app/components/DetailPageHeader';
import { ForbiddenState } from '@app/components/ForbiddenState';
import { NotFoundState } from '@app/components/NotFoundState';
import { PageActionsMenu } from '@app/components/PageActionsMenu';
import { Klass } from '@app/model/Klass';
import {
  Submission,
  SurveyView,
  TranslationSummary,
  isTranslatable,
  isTranslated,
  respondentLabel,
  submissionComments
} from '@app/model/Survey';
import { SurveyDistribution } from '@app/Classes/SurveyDistribution';
import { SurveyMatrix } from '@app/Classes/SurveyMatrix';
import { SurveyScores } from '@app/Classes/SurveyParts';
import { SubmissionDetail } from '@app/Classes/SurveyRespondent';
import { SurveyRespondents } from '@app/Classes/SurveyRespondents';
import { loadKlassByOfferingId } from '@app/store/entityLookups';
import { ApiError, isForbidden, isNotFound, toApiError } from '@app/utils/ApiError';

/**
 * Every answer on a class's survey, by question and by respondent.
 *
 * ## Why this is a page and not a tab
 *
 * Everything here is built out of individual submissions, and a submission names the student who
 * made it - {@link Submission.name}, their email, their employer, and which comment is theirs. The
 * service answers those to `admin` only, so this is `admin` only, declared on the route in
 * routes.tsx rather than checked here: a page added to a restricted address cannot then ship
 * unguarded. See RequireRole.
 *
 * The half of a survey that is everybody's - the six scores and the comments with the respondents
 * taken off - is the Survey tab on the class page, which is where a reader without the role stops.
 * Neither view repeats the other: the comments are anonymised over there and attributed here.
 *
 * ## The three layouts
 *
 * The same data three ways, together on purpose, because they answer different questions:
 *
 * - **Matrix** - every answer at once. A row that is uniformly weak is a problem with the course; a
 *   column that is uniformly weak is one unhappy student.
 * - **Distribution** - the aggregate per question. Two fives and two ones average to the same three
 *   as four threes, and only here do those look different.
 * - **Respondents** - one student at a time, which is the only question the other two cannot answer.
 *
 * ## Loading
 *
 * Three requests, each depending on the one before: the class by its offering id (which is what the
 * address carries), the survey by the class's own id, and the submissions by the survey's. The
 * class is read through the store, so arriving from the class page - which is how a reader gets
 * here - costs nothing for the first of them.
 */
/**
 * What the service did, in a sentence.
 *
 * Every count is worth a clause because they mean different things to whoever pressed the option:
 * a class that comes back entirely "already in English" is a success where nothing on the page
 * changed, and without saying so the action looks broken.
 */
export const translationSummaryText = (summary: TranslationSummary): string => {
  const parts: string[] = [];
  if (summary.translated > 0) parts.push(`${summary.translated} translated`);
  if (summary.alreadyInEnglish > 0) parts.push(`${summary.alreadyInEnglish} already in English`);
  if (summary.skipped > 0) parts.push(`${summary.skipped} left as they were`);
  if (summary.failed > 0) parts.push(`${summary.failed} could not be translated`);

  if (parts.length === 0) return 'There were no comments to translate.';
  return `${summary.comments} comment${summary.comments === 1 ? '' : 's'}: ${parts.join(', ')}.`;
};

export const ClassSurveyPage: React.FunctionComponent = () => {
  const { oid } = useParams();
  const { addNotification } = useNotification();

  const [klass, setKlass] = React.useState<Klass | undefined>();
  const [survey, setSurvey] = React.useState<SurveyView | undefined>();
  const [submissions, setSubmissions] = React.useState<Submission[]>([]);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<ApiError | undefined>();
  /** No class carries this offering id, or it carries no survey. Told apart below. */
  const [missing, setMissing] = React.useState<'class' | 'survey' | undefined>();
  /**
   * The service refused the submissions although the route let this session through.
   *
   * Not an error: the token is a claim the browser cannot check, and a realm that has narrowed the
   * role since it was issued is the ordinary way to get here. The endpoint is the authority.
   */
  const [refused, setRefused] = React.useState<boolean>(false);

  const [activeTab, setActiveTab] = React.useState<string | number>('matrix');
  /** The submission a matrix column heading opened, shown over the page. */
  const [openSubmission, setOpenSubmission] =
    React.useState<{ submission: Submission; index: number } | undefined>();

  /** The "translate these again?" question, open only when there is already work to redo. */
  const [confirmRedo, setConfirmRedo] = React.useState<boolean>(false);
  const [translating, setTranslating] = React.useState<boolean>(false);

  /** `bypassCache` is what Refresh passes: read the class from the service whatever is held. */
  const load = React.useCallback(async (bypassCache = false) => {
    if (!oid) return;
    setLoading(true);
    setError(undefined);
    setMissing(undefined);
    setRefused(false);

    try {
      const loadedKlass = await loadKlassByOfferingId(oid, bypassCache);
      setKlass(loadedKlass);

      let view: SurveyView;
      try {
        view = await SurveyApi.getByKlass(loadedKlass.id);
      } catch (surveyError: unknown) {
        // Told apart from a missing class on purpose: "this class has no survey yet" and "there is
        // no such class" send the reader to two completely different places.
        if (isNotFound(surveyError)) { setMissing('survey'); return; }
        throw surveyError;
      }
      setSurvey(view);

      try {
        setSubmissions(await SurveyApi.getSubmissions(view.id));
      } catch (submissionError: unknown) {
        if (isForbidden(submissionError)) setRefused(true);
        else throw submissionError;
      }
    } catch (loadError: unknown) {
      if (isNotFound(loadError)) setMissing('class');
      else setError(toApiError(loadError));
    } finally {
      setLoading(false);
    }
  }, [oid]);

  React.useEffect(() => {
    load();
  }, [load]);

  /*
   * What the translation would be acting on. Read off the submissions rather than off the survey's
   * anonymised lists because this page holds both and the submissions are where the two halves of
   * each comment live as fields.
   */
  const comments = React.useMemo(() => submissionComments(submissions), [submissions]);
  /** Something here is not already English, so there is work for the service to do. */
  const hasWork = comments.some(isTranslatable);
  /** The translation has been run over this class before, so pressing it again would redo it. */
  const wasTranslated = comments.some(isTranslated);

  const translate = React.useCallback(async (force: boolean) => {
    // The class's own id, not the offering id the address carries - see SurveyApi.translateComments.
    if (!klass?.id) return;
    setConfirmRedo(false);
    setTranslating(true);
    try {
      const summary = await SurveyApi.translateComments(klass.id, force);
      // Reloaded rather than patched in place: the service decides what each comment's English is,
      // including the comments it left alone, and reading it back is the only way to show what it
      // actually did.
      await load(true);
      addNotification(
        'Comments translated',
        summary.failed > 0 ? AlertVariant.warning : AlertVariant.success,
        translationSummaryText(summary)
      );
    } catch (translateError: unknown) {
      addNotification(
        'Could not translate the comments',
        AlertVariant.danger,
        toApiError(translateError).message
      );
    } finally {
      setTranslating(false);
    }
  }, [klass?.id, load, addNotification]);

  if (loading) {
    return <PageSection hasBodyWrapper><Bullseye><Spinner aria-label="Loading the survey" /></Bullseye></PageSection>;
  }

  if (missing === 'class') {
    return <NotFoundState />;
  }

  if (error) {
    return (
      <PageSection hasBodyWrapper>
        <DataLoadError error={error} entityLabel="this class's survey" onRetry={() => load(true)} />
      </PageSection>
    );
  }

  // The route let the session through on the strength of its token and the service did not agree.
  // Said plainly rather than shown as an empty grid, which would read as "nobody answered".
  if (refused) {
    return (
      <ForbiddenState titleText="The submissions were refused">
        Your session claims the <code>admin</code> role but the service did not accept it for this
        class&apos;s submissions. Signing in again picks up whatever roles the realm now holds for
        you. The scores and the comments are on the class&apos;s Survey tab either way.
      </ForbiddenState>
    );
  }

  // The layout already gets Back and the breadcrumb right by itself - both walk one segment up,
  // which from /classes/4711/survey is the class - so this page declares neither.
  const title = klass ? `${klass.offeringId} - ${klass.course?.sku ?? ''}` : (oid ?? '');

  const header = (
    <DetailPageHeader
      title={title}
      actions={
        <PageActionsMenu
          onRefresh={() => load(true)}
          items={[{
            key: 'translate-comments',
            label: 'Translate comments',
            // Inactive when every comment on the class was already written in English, because
            // there would be nothing for the service to do. A class nobody has translated yet and
            // a class already translated both leave it active - the second is what force is for.
            isDisabled: !hasWork || translating,
            onClick: () => (wasTranslated ? setConfirmRedo(true) : translate(false))
          }]}
        />
      }
    >
      <Label color="blue">Survey</Label>
    </DetailPageHeader>
  );

  if (missing === 'survey' || !survey) {
    return (
      <>
        {header}
        <PageSection hasBodyWrapper>
          <EmptyState headingLevel="h4" icon={OutlinedCommentsIcon} titleText="No survey for this class">
            <EmptyStateBody>
              Surveys are imported from the survey platform after a class is delivered, so a class
              that has just run will not have one yet. Nothing here can create one.
            </EmptyStateBody>
          </EmptyState>
        </PageSection>
      </>
    );
  }

  const hasSubmissions = submissions.length > 0;

  return (
    <>
      {header}
      <PageSection hasBodyWrapper>
        <SurveyScores survey={survey} enrolled={klass?.totalEnrolled} submissions={submissions} />

        {hasSubmissions ? (
          <Tabs
            activeKey={activeTab}
            onSelect={(_event, key) => setActiveTab(key)}
            isSubtab
            // Only the tab in front is in the document. Without this PatternFly renders every pane
            // and hides the inactive ones, which here means three copies of every question label in
            // the accessibility tree and a matrix nobody is looking at.
            mountOnEnter
            unmountOnExit
            aria-label="How to read the survey"
            role="region"
          >
            <Tab eventKey="matrix" title={<TabTitleText>Matrix</TabTitleText>}>
              <div className="cas-survey-pane">
                <SurveyMatrix
                  survey={survey}
                  submissions={submissions}
                  onSelectRespondent={(submission, index) => setOpenSubmission({ submission, index })}
                />
              </div>
            </Tab>
            <Tab eventKey="distribution" title={<TabTitleText>Distribution</TabTitleText>}>
              <div className="cas-survey-pane">
                <SurveyDistribution survey={survey} submissions={submissions} />
              </div>
            </Tab>
            <Tab eventKey="respondents" title={<TabTitleText>Respondents</TabTitleText>}>
              <div className="cas-survey-pane">
                <SurveyRespondents submissions={submissions} />
              </div>
            </Tab>
          </Tabs>
        ) : (
          <EmptyState headingLevel="h4" icon={CommentIcon} titleText="No submissions">
            <EmptyStateBody>
              The survey for this class is here but holds no student answers. Either nobody
              answered, or the message that carried them has not arrived yet. The scores above are
              the survey platform&apos;s own and do not depend on them.
            </EmptyStateBody>
          </EmptyState>
        )}
      </PageSection>

      {/*
        * Asked only when there is something to redo. The service leaves an already translated
        * comment alone unless it is told not to, so this is the one place `force` is sent from and
        * pressing the option on an untranslated class needs no question at all.
        */}
      <Modal
        isOpen={confirmRedo}
        variant={ModalVariant.small}
        onClose={() => setConfirmRedo(false)}
        aria-label="Translate these comments again?"
      >
        <ModalHeader title="Translate these comments again?" />
        <ModalBody>
          The comments on this survey already carry an English version. Translating again replaces
          every one of them, including any that were translated before. The originals are untouched
          either way - a student&apos;s own words are the record and nothing here overwrites them.
        </ModalBody>
        <ModalFooter>
          <Button variant="primary" onClick={() => translate(true)} isDisabled={translating}>
            Translate again
          </Button>
          <Button variant="link" onClick={() => setConfirmRedo(false)}>Cancel</Button>
        </ModalFooter>
      </Modal>

      {openSubmission && (
        <Modal
          isOpen
          variant={ModalVariant.large}
          onClose={() => setOpenSubmission(undefined)}
          aria-label="One respondent's submission"
        >
          <ModalHeader
            title={`Submission — ${respondentLabel(openSubmission.submission, openSubmission.index)}`}
          />
          <ModalBody>
            <SubmissionDetail submission={openSubmission.submission} index={openSubmission.index} />
          </ModalBody>
        </Modal>
      )}
    </>
  );
};

export default ClassSurveyPage;
